import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TemplateForm } from "@/components/whatsapp/TemplateForm";
import {
  useWhatsAppMessageTemplates,
  useCreateWhatsAppTemplate,
  useUpdateWhatsAppTemplate,
  useDeleteWhatsAppTemplate,
  type WhatsAppMessageTemplate,
  type CreateTemplateInput,
  type TemplateMetaStatus,
} from "@/hooks/useWhatsAppMessageTemplates";

function StatusBadge({ status }: { status: TemplateMetaStatus }) {
  const { t } = useTranslation("whatsappTemplates");
  const variantMap: Record<TemplateMetaStatus, "default" | "secondary" | "destructive" | "outline"> = {
    APPROVED: "default",
    PENDING: "secondary",
    DRAFT: "outline",
    REJECTED: "destructive",
    PAUSED: "secondary",
    DISABLED: "secondary",
  };
  const labelMap: Record<TemplateMetaStatus, string> = {
    DRAFT: t("statusDraft"),
    PENDING: t("statusPending"),
    APPROVED: t("statusApproved"),
    REJECTED: t("statusRejected"),
    PAUSED: t("statusPaused"),
    DISABLED: t("statusDisabled"),
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

export function WhatsAppTemplatesPage() {
  const { t } = useTranslation("whatsappTemplates");
  const { toast } = useToast();
  const { data: templates = [], isLoading } = useWhatsAppMessageTemplates();

  const createMutation = useCreateWhatsAppTemplate();
  const updateMutation = useUpdateWhatsAppTemplate();
  const deleteMutation = useDeleteWhatsAppTemplate();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<WhatsAppMessageTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppMessageTemplate | null>(null);

  async function handleCreate(input: CreateTemplateInput) {
    try {
      await createMutation.mutateAsync(input);
      toast({ title: t("successCreated") });
      setCreateOpen(false);
    } catch {
      toast({ title: t("errorCreate"), variant: "destructive" });
    }
  }

  async function handleUpdate(input: CreateTemplateInput) {
    if (!editTemplate) return;
    try {
      await updateMutation.mutateAsync({ id: editTemplate.id, input });
      toast({ title: t("successUpdated") });
      setEditTemplate(null);
    } catch {
      toast({ title: t("errorUpdate"), variant: "destructive" });
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast({ title: t("successDeleted") });
      setDeleteTarget(null);
    } catch {
      toast({ title: t("errorDelete"), variant: "destructive" });
    }
  }

  const categoryLabel = (c: string) => {
    if (c === "MARKETING") return t("categoryMarketing");
    if (c === "UTILITY") return t("categoryUtility");
    return t("categoryAuthentication");
  };

  const hasPending = templates.some((tpl) => tpl.metaStatus === "PENDING");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("pageTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("pageDesc")}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasPending && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {t("syncingStatus")}
            </span>
          )}
          <Button size="sm" className="gap-1" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("newTemplate")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("loadingTemplates")}</p>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center space-y-1">
          <p className="text-sm font-medium">{t("emptyState")}</p>
          <p className="text-xs text-muted-foreground">{t("emptyStateDesc")}</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("tableNameCol")}</TableHead>
                <TableHead>{t("tableCategoryCol")}</TableHead>
                <TableHead>{t("tableLanguageCol")}</TableHead>
                <TableHead>{t("tableStatusCol")}</TableHead>
                <TableHead className="w-20">{t("tableActionsCol")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell className="font-mono text-sm">{tpl.name}</TableCell>
                  <TableCell className="text-sm">{categoryLabel(tpl.category)}</TableCell>
                  <TableCell className="text-sm">{tpl.language}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <StatusBadge status={tpl.metaStatus} />
                      {tpl.metaRejectionReason && (
                        <p className="text-xs text-destructive">{tpl.metaRejectionReason}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setEditTemplate(tpl)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setDeleteTarget(tpl)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("newTemplate")}</DialogTitle>
          </DialogHeader>
          <TemplateForm onSubmit={handleCreate} isLoading={createMutation.isPending} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTemplate} onOpenChange={(open) => !open && setEditTemplate(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("editTemplate")}</DialogTitle>
          </DialogHeader>
          {editTemplate && (
            <TemplateForm
              isEdit
              defaultValues={{
                name: editTemplate.name,
                language: editTemplate.language,
                category: editTemplate.category,
                headerType: editTemplate.headerType ?? "",
                headerText: editTemplate.headerText ?? "",
                headerMediaUrl: editTemplate.headerMediaUrl ?? "",
                bodyText: editTemplate.bodyText,
                footerText: editTemplate.footerText ?? "",
                buttons: editTemplate.buttons ?? [],
                variableMapping: editTemplate.variableMapping,
              }}
              onSubmit={handleUpdate}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteTemplate")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirm", { name: deleteTarget?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel", { defaultValue: "Cancelar" })}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {t("common:delete", { defaultValue: "Excluir" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
